import { useListContext, useRecordContext, useTranslate } from "ra-core";
import { DataTable } from "@/components/admin/data-table";
import { ReferenceField } from "@/components/admin/reference-field";

import type { Sponsoring } from "../types";
import { ContractTypeName } from "./ContractTypeName";
import { EndDateField } from "./EndDateStatus";
import { SaleName } from "./SaleName";
import { sponsoringViewBasePath, type SponsoringView } from "./sponsoringViews";
import { StageBadge } from "./StageBadge";

/**
 * Table view of the sponsorings. Clicking a row opens the show dialog
 * (`${basePath}/:id/show`), which SponsoringList renders on top of the list.
 * Rows can be selected for bulk export / delete. Balsponsoren have no
 * contract type, so that column is dropped on their overview.
 */
export const SponsoringTable = ({
  view = "sponsorings",
}: {
  view?: SponsoringView;
}) => {
  const translate = useTranslate();
  const { data, isPending, error } = useListContext<Sponsoring>();

  if (isPending || error) return null;

  if (!data?.length) {
    return (
      <div
        className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground"
        role="status"
      >
        {translate("resources.sponsorings.list.no_results")}
      </div>
    );
  }

  return (
    <DataTable<Sponsoring>
      rowClick={(id) => `${sponsoringViewBasePath[view]}/${id}/show`}
      className="bg-card"
      // The overviews do not have the same columns, so they cannot share the
      // stored column order / visibility of the default one.
      storeKey={
        view === "sponsorings" ? undefined : `sponsorings.${view}.datatable`
      }
    >
      <DataTable.Col
        source="company_id"
        label="resources.sponsorings.fields.company_id"
        disableSort
      >
        <ReferenceField
          source="company_id"
          reference="companies"
          link={false}
          className="font-medium"
        />
      </DataTable.Col>
      {view === "ball" ? null : (
        <DataTable.Col
          source="contract_type_id"
          label="resources.sponsorings.fields.contract_type_id"
          disableSort
        >
          <ContractTypeName />
        </DataTable.Col>
      )}
      <DataTable.Col source="stage" label="resources.sponsorings.fields.stage">
        <StageCell />
      </DataTable.Col>
      <DataTable.Col
        source="end_date"
        label="resources.sponsorings.fields.end_date"
      >
        <EndDateCell />
      </DataTable.Col>
      <DataTable.Col
        source="sales_id"
        label="resources.sponsorings.fields.sales_id"
        disableSort
      >
        <ReferenceField source="sales_id" reference="sales" link={false}>
          <SaleName />
        </ReferenceField>
      </DataTable.Col>
    </DataTable>
  );
};

const StageCell = () => {
  const record = useRecordContext<Sponsoring>();
  if (!record) return null;
  return <StageBadge stage={record.stage} />;
};

const EndDateCell = () => {
  const record = useRecordContext<Sponsoring>();
  if (!record) return null;
  return <EndDateField record={record} />;
};
